/**
 * THE ROTOM PHONE (plan §9.11): the player side of the web tier, as the in-game item in a browser tab.
 *
 * A layout route around every player app. On a desktop the phone stands on a backdrop of the hub, drawn in
 * the item's own colours — the orange body, the cyan rim, the dark screen and its speaker strip, the antenna
 * from the item's sprite (`scripts/web/rotom_phone_sprite.py`); on a phone it IS the screen, edge to edge.
 * Inside: a status bar (the time, the link to the game, a note when music plays), the open app, and a home
 * bar. What the music has to say while another app is open — a cue, a change of area — is said here.
 *
 * THE MUSIC IS NOT IN HERE. The engine, its socket and the service worker live in `AudioCompanionProvider`
 * above every route, so this shell can come and go (the staff console is outside it) and the apps inside it
 * can swap freely without a track, a crossfade or a shared instance noticing.
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

import { useSession } from '../auth/SessionProvider';
import { useAudioCompanionState, type AudioCompanion } from '../audio/AudioCompanionProvider';
import { AreaAnnouncement, CueNotification } from '../audio/notices';
import { moodOf, statusOf } from '../audio/status';
import { PHONE_APPS, PHONE_HOME, phoneAppFor, type PhoneAppId } from './apps';
import { phoneFit, type PhoneFit } from './fit';
import { Glyph } from './icons';

import './phone.css';

/** The tab's title for a phone route; null for Music, which names what is playing itself (`AudioPage`). */
export function phoneTitleFor(pathname: string): string | null {
  const app = phoneAppFor(pathname);
  if (app?.id === 'music') return null;
  if (app !== null) return `${app.name} — Rotom Phone`;
  return pathname === '/link' || pathname.startsWith('/link/') ? 'Link your account — Rotom Phone' : 'Rotom Phone';
}

function measureFit(): PhoneFit {
  return phoneFit(globalThis.innerWidth ?? 1440, globalThis.innerHeight ?? 900);
}

/** The window's fit, kept current on resize; measured before paint so a large screen never flashes a small phone. */
function usePhoneFit(): PhoneFit {
  const [fit, setFit] = useState<PhoneFit>(measureFit);
  useLayoutEffect(() => {
    const update = (): void => setFit(measureFit());
    update();
    globalThis.addEventListener('resize', update);
    return () => globalThis.removeEventListener('resize', update);
  }, []);
  return fit;
}

export function PhoneShell(): React.JSX.Element {
  const companion = useAudioCompanionState();
  const { authError } = useSession();
  const { pathname } = useLocation();
  const mood = companion.active ? moodOf(companion.state) : 'day';
  const fit = usePhoneFit();
  // The device and its caption zoom together; phone.css reads these two (see fit.ts).
  const sizing = { '--ph-zoom': String(fit.zoom), ...(fit.mode === 'device' ? { '--ph-body-h': `${fit.bodyHeight}px` } : {}) } as React.CSSProperties;

  // The tab says which app is open, and gives the site's own name back when the phone is put away.
  useEffect(() => {
    const title = phoneTitleFor(pathname);
    if (title !== null) globalThis.document.title = title;
    return () => {
      globalThis.document.title = 'CobblemonDimensions';
    };
  }, [pathname]);

  return (
    <div className="ph-stage" data-testid="phone-stage" data-mood={mood} data-fit={fit.mode} data-decor={fit.decor ? 'on' : 'off'} style={sizing}>
      <div className="ph-backdrop" aria-hidden="true" />
      <div className="ph-caption" aria-hidden="true">
        <p className="ph-caption-word">CobblemonDimensions</p>
        <p className="ph-caption-line">Your Rotom Phone: your soundtrack, your stats and the map of the Nexus — keep it open while you play.</p>
      </div>
      <div className="ph-device" data-testid="phone">
        <div className="ph-antenna" aria-hidden="true" />
        <div className="ph-body">
          <div className="ph-screen">
            <StatusBar companion={companion} />
            {authError !== null && (
              <p className="ph-notice" role="alert" data-testid="auth-error">
                {authError}
              </p>
            )}
            <CueNotification companion={companion} />
            <div className="ph-view" data-path={pathname}>
              <Outlet />
            </div>
            <HomeBar atHome={pathname === PHONE_HOME} />
          </div>
        </div>
        <div className="ph-stub" aria-hidden="true" />
      </div>
      <AreaAnnouncement companion={companion} />
    </div>
  );
}

/**
 * One app's screen: its title bar, with the way back to the home screen, and its content scrolling under it.
 * `title` overrides the registry's name for a screen that is not an app of its own (linking an account).
 */
export function AppScreen({ app, title, children }: { readonly app: PhoneAppId | 'link'; readonly title?: string; readonly children: React.ReactNode }): React.JSX.Element {
  const name = title ?? PHONE_APPS.find((candidate) => candidate.id === app)?.name ?? app;
  return (
    <section className={`ph-app ph-app-${app}`} data-testid={`phone-app-${app}`} aria-labelledby={`ph-app-title-${app}`}>
      <header className="ph-appbar">
        <Link to={PHONE_HOME} className="ph-back" aria-label="Back to the home screen" data-testid="phone-back">
          <Glyph name="back" size={16} />
        </Link>
        <h1 id={`ph-app-title-${app}`} className="ph-app-title">
          {name}
        </h1>
      </header>
      <div className="ph-appbody">{children}</div>
    </section>
  );
}

function StatusBar({ companion }: { readonly companion: AudioCompanion }): React.JSX.Element {
  const time = useClock();
  const { state } = companion;
  const ready = companion.active && state.phase === 'ready';
  const status = ready ? statusOf(state) : null;
  const playing = state.enabled && state.snapshot.state === 'playing';
  const label = status === null ? 'Not connected' : status.label;
  return (
    <div className="ph-status" data-testid="phone-status" data-tone={status?.tone ?? 'off'}>
      <span className="ph-time">{time}</span>
      <span className="ph-notch" aria-hidden="true" />
      <span className="ph-indicators">
        {playing && (
          <Link to="/audio" className="ph-playing" aria-label="Music is playing — open Music" data-testid="phone-status-playing">
            ♪
          </Link>
        )}
        <span className="ph-link" title={label} aria-label={label}>
          <span className="ph-link-dot" aria-hidden="true" />
          <span className="ph-link-label">{status === null ? 'Offline' : status.tone === 'live' ? 'Live' : status.tone === 'bad' ? 'Off' : '…'}</span>
        </span>
      </span>
    </div>
  );
}

function HomeBar({ atHome }: { readonly atHome: boolean }): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <nav className="ph-homebar" aria-label="Phone">
      <button type="button" className="ph-homebutton" onClick={() => void navigate(PHONE_HOME)} aria-label="Home screen" aria-current={atHome ? 'page' : undefined} data-testid="phone-home-button">
        <span aria-hidden="true" />
      </button>
    </nav>
  );
}

/** The time for the status bar, to the minute, in the reader's own locale and zone. */
function useClock(): string {
  const format = (): string => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const [time, setTime] = useState(format);
  useEffect(() => {
    const timer = setInterval(() => setTime(format()), 15_000);
    return () => clearInterval(timer);
  }, []);
  return time;
}
